import { createContext, useEffect, useContext, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import packageJson from "../../package.json";
import {
  AUTO_REFRESH_INTERVAL_HOURS,
  CONFIG_FETCH_INTERVAL_MS,
  HELM_STATUS_PUBLISH_INTERVAL_MS,
} from "utils/constants";
import {
  getBrowserMemorySize,
  getBrowserMemoryUsageValue,
} from "screens/settings/utils";
import { isCanvasSupported } from "utils/canvas";
import { DashboardMode, GuidanceMode } from "types/enums";
import { RequestTopics, Topics } from "services/topics";
import { MqttClientContext } from "services/mqtt-client";
import { StorageMethod } from "hooks/use-local-storage";
import useMqttApi from "hooks/use-mqtt-api";
import { useStore } from "hooks/use-store";
import { useBrowserSpeed } from "hooks/use-browser-speed";

const AppContext = createContext<any>(null);

const AppProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const client = useContext(MqttClientContext);

  // Monitor browser speed
  useBrowserSpeed();

  // Track session start time
  const sessionStartTimestamp = useRef(new Date());

  // Track initialization state
  const [isClientConnected, setIsClientConnected] = useState(false);
  const [hasFetchedConfig, setHasFetchedConfig] = useState(false);
  const [hasFetchedLocalStorage, setHasFetchedLocalStorage] = useState(false);

  // Store values from the global store
  const config = useStore((state) => state.config);
  const hasEcoSog = useStore((state) => state.hasEcoSog);
  const setConfig = useStore((state) => state.setConfig);
  const setDashboardMode = useStore((state) => state.setDashboardMode);
  const setGuidanceMode = useStore((state) => state.setGuidanceMode);
  const setRpmSogMode = useStore((state) => state.setRpmSogMode);
  const setFeatureFlags = useStore((state) => state.setFeatureFlags);
  const setSafetySpeedEnabled = useStore(
    (state) => state.setSafetySpeedEnabled
  );
  const setEcoSpeedEnabled = useStore((state) => state.setEcoSpeedEnabled);

  const { fetch: fetchConfig } = useMqttApi({
    requestTopic: RequestTopics.Config,
    enabled: false,
    onSuccess: (data) => {
      setConfig(data.data);
      setHasFetchedConfig(true);
    },
    onError: (error) => {
      toast.error(error?.message ?? "Could not get config");
    },
  });

  const { data: localStorage, fetch: fetchLocalStorage } = useMqttApi({
    requestTopic: RequestTopics.LocalStorage,
    enabled: false,
    request: {
      method: StorageMethod.GetAll,
    },
    onSuccess: () => {
      setHasFetchedLocalStorage(true);
    },
    onError: (error) => {
      toast.error(error?.message ?? "Could not get local storage");
    },
  });

  // Prefetch config and local storage
  useEffect(() => {
    setIsClientConnected(!!client?.connected);
    if (!client?.connected) {
      return;
    }

    // Initial prefetch
    fetchConfig();
    fetchLocalStorage();

    // Keep both config and local storage fresh to avoid stale state decisions
    const configRefetchInterval = setInterval(() => {
      fetchConfig();
      fetchLocalStorage();
    }, CONFIG_FETCH_INTERVAL_MS);

    // Publish helm status regularly to inform backend that frontend is online
    const helmStatusPublisherInterval = setInterval(() => {
      const body = {
        software_version: packageJson.version,
        session_start_timestamp: sessionStartTimestamp.current.toISOString(),
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        user_agent: window?.navigator?.userAgent ?? "unknown",
        screen_width: window?.screen?.width ?? 0,
        screen_height: window?.screen?.height ?? 0,
        pixel_ratio: window?.devicePixelRatio ?? 0,
        memory_usage: getBrowserMemoryUsageValue(),
        memory_size: getBrowserMemorySize(),
        html5_canvas_support: isCanvasSupported(),
        os: window?.navigator?.platform ?? "unknown",
      };

      client.publish(Topics.HelmStatus, JSON.stringify(body), {
        qos: 0,
        retain: true,
      });
    }, HELM_STATUS_PUBLISH_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      clearInterval(configRefetchInterval);
      clearInterval(helmStatusPublisherInterval);
    };
  }, [client]);

  // Set initial guidance mode, dashboard mode and RPM/SOG mode
  useEffect(() => {
    const localGuidanceMode = localStorage?.data?.guidance_mode;
    const localDashboardMode = localStorage?.data?.dashboard_mode;
    const localRpmSogMode = localStorage?.data?.rpm_sog_mode;
    const localSafetySpeedEnabled = localStorage?.data?.safety_speed_enabled;
    const localEcoSpeedEnabled = localStorage?.data?.eco_speed_enabled;

    // Set guidance mode (safety or eco)
    if (
      config?.guidance_mode &&
      config?.guidance_mode !== GuidanceMode.Automatic
    ) {
      setGuidanceMode(hasEcoSog ? config.guidance_mode : GuidanceMode.Safety);
    } else {
      setGuidanceMode(
        hasEcoSog
          ? localGuidanceMode || GuidanceMode.Safety
          : GuidanceMode.Safety
      );
    }

    // Set dashboard mode (gauges, blocks or chart)
    if (
      config?.dashboard_mode &&
      config?.dashboard_mode !== DashboardMode.Automatic
    ) {
      // if dashboard mode is set in config, use that
      setDashboardMode(config.dashboard_mode);
    } else {
      // else use the local storage value
      setDashboardMode(localDashboardMode ?? DashboardMode.Gauges);
    }

    // Set RPM/SOG mode
    if (localRpmSogMode) {
      setRpmSogMode(localRpmSogMode);
    }

    // Set safety/eco speed enabled from local storage
    if (localSafetySpeedEnabled !== undefined) {
      setSafetySpeedEnabled(localSafetySpeedEnabled);
    }
    if (localEcoSpeedEnabled !== undefined) {
      setEcoSpeedEnabled(localEcoSpeedEnabled);
    }
  }, [localStorage, config, hasEcoSog]);

  // Sync feature flags from config
  useEffect(() => {
    const ft = config?.company_subscription?.features_transformed;

    // if features are not available in config, keep existing feature flag defaults
    if (!ft) return;

    setFeatureFlags({
      safetySpeed: !!ft?.["safety-speed"],
      ecoSpeed: !!ft?.["eco-speed"],
      seaSickness: !!ft?.["sea-sickness"],
    });

    if (ft?.["safety-speed"] === undefined) {
      setSafetySpeedEnabled(false);
    } else if (localStorage?.data?.safety_speed_enabled === undefined) {
      setSafetySpeedEnabled(true);
    }

    if (ft?.["eco-speed"] === undefined) {
      setEcoSpeedEnabled(false);
    } else if (localStorage?.data?.eco_speed_enabled === undefined) {
      setEcoSpeedEnabled(true);
    }
  }, [config, localStorage]);

  // Set language
  useEffect(() => {
    const localLanguage = localStorage?.data?.language;

    if (i18n) {
      i18n.changeLanguage(localLanguage || "en");
    }
  }, [i18n, localStorage]);

  // Refresh page every 24h to ensure newest version is loaded
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      window.location.reload();
    }, AUTO_REFRESH_INTERVAL_HOURS * 60 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  return (
    <AppContext.Provider
      value={{
        hasFetchedConfig,
        hasFetchedLocalStorage,
        isClientConnected,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export { AppProvider, AppContext };
